import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { FileServerService } from '@app/core/services/file-server.service';
import { ModalService } from '@app/core/services/modal.service';
import { MergeDocument, PdfService } from '@app/services/pdf.service';
import { StateService } from '@app/core/services/state.service';
import { DocumentsMergedPreviewComponent } from '@app/views/components/documents/documents-merged-preview/documents-merged-preview.component';

@Component({
    selector: 'tt-documents-grid',
    templateUrl: './documents-grid.component.html',
    styleUrls: ['./documents-grid.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class DocumentsGridComponent extends ComponentBaseComponent implements OnChanges {
    @Input()
    params?: any;

    @Input()
    rememberId: string = 'documents_grid';

    @Output()
    gridReady = new EventEmitter<GridOptions>();

    constructor(
        private file: FileServerService,
        private pdf: PdfService,
        private modal: ModalService,
        private dialog: MatDialog,
        private state: StateService,
    ) {
        super();
    }

    public grid?: GridOptions;

    private createGrid() {
        this.grid = new Grid()
            .setRememberId(this.rememberId)
            .setLoadDataFunction({ method: 2972, parameters: () => this.params })
            .setCustomToolbarButtons([
                {
                    name: 'customersmenu_documents_new',
                    text: 'customersmenu_documents_new',
                    func: () => {
                        this.state.go('documents', {
                            argtype: this.params.argtype,
                            argvalue: this.params.argvalue,
                        });
                    },
                    icon: 'fa-plus',
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-l',
                    translate: true,
                    disabled: () => (this.state.getCurrentName() === 'documents' ? 'hidden' : false),
                },
                {
                    name: 'merged_preview',
                    text: 'merge_files_and_preview',
                    translate: true,
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-l',
                    icon: 'far fa-file-search',
                    disabled: () => (this.params.doctype !== 'd' ? 'hidden' : (this.grid?.gridfunc?.getIsSelectedRows().length ?? 0) < 2),
                    func: async () => {
                        const docfilenames = this.grid!.gridfunc!.getIsSelectedRows().map((row) => row['docfilename']);

                        const progress = this.modal.openProgressDialog({ value: 0, max: docfilenames.length, indeterminate: true, rememberId: 'w_documents.progress.merge' });

                        progress.updateIndeterminate(true);

                        let count = 0;

                        const fileArrayBuffers = await Promise.all(
                            docfilenames.map(async (label) => {
                                try {
                                    const arraybuffer = await this.file.downloadFile({ fileName: label });
                                    progress.updateValue((count = count + 1));

                                    return arraybuffer;
                                } catch (error) {
                                    progress.updateStatus('invalid');
                                    progress.updateInvalidText(label);
                                    console.error(error);
                                }

                                return null;
                            }),
                        );
                        try {
                            // The merger populates page bookkeeping for these content-only inputs.
                            const mergeDocuments = fileArrayBuffers.filter((arrayBuffer): arrayBuffer is ArrayBuffer => arrayBuffer !== null).map((arrayBuffer) => ({ content: arrayBuffer, contentType: 'application/pdf' }) as MergeDocument);

                            const pdf = await this.pdf.mergePDFs(mergeDocuments, { includeTableOfContents: false, fileName: this.getFileName() });
                            this.dialog.open(DocumentsMergedPreviewComponent, { data: { src: this.getUrl(pdf, 'documents_merged_pdf.pdf'), fileName: this.getFileName() } });
                            progress.updateIndeterminate(false);
                            progress.dialogRef.close();
                        } catch (error) {
                            console.error(error);
                        }
                    },
                },
            ]);
    }

    private getFileName(): string {
        return `${new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 16).replaceAll('T', '_').replaceAll(':', '').replaceAll('-', '')}_${this.state.getCurrentName()}_${Object.entries(this.state.getStateParams())
            .filter((val) => val[0] !== '#')
            .reduce((acc, val) => acc + `${val[1]}_`, '')}merged_documents.pdf`;
    }

    private getUrl(base64Data: string, filename: string) {
        const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const file = new File([blob], filename, { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(file);

        return blobUrl;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            if (!this.grid) this.createGrid();
            this.grid?.gridfunc?.refreshToolbarBtnDisability();
        }
    }
}
